import {
  computed,
  type MaybeRefOrGetter,
  onBeforeUnmount,
  onMounted,
  ref,
  toValue,
  watch,
} from "vue";
import { usePrefersReducedMotion } from "../composables/useSlideshow";

export const ROTATION_MS = 9000;

/**
 * One item at a time, moving on every `ms`. Waits while the block is hovered, focused,
 * less than half on screen or in a hidden tab. Hover and focus restart the full time;
 * the others resume where it stopped, as the chip fill (a CSS animation of `ms`) does.
 * Never moves with reduced motion (WCAG 2.2.2).
 *
 * Bind `el` to the block (`:ref="rotation.el"`); it listens on it itself.
 */
export function useRotation(
  count: MaybeRefOrGetter<number>,
  ms: number = ROTATION_MS
) {
  const index = ref(0);
  const el = ref<HTMLElement | null>(null);
  /** The visitor pressed pause. */
  const paused = ref(false);
  /** Stopped for good, e.g. at the first tap or swipe on a phone. */
  const stopped = ref(false);
  const hovered = ref(false);
  const focused = ref(false);
  /** At least half the block in view. */
  const onScreen = ref(false);
  const tabVisible = ref(true);
  const reducedMotion = usePrefersReducedMotion();

  /** Rotation can run at all; the pause button shows only then. */
  const rotates = computed(
    () => toValue(count) > 1 && !reducedMotion.value && !stopped.value
  );
  /** Held by the visitor, which restarts the full time when let go. */
  const held = computed(() => hovered.value || focused.value);
  /** The timer is counting right now. */
  const running = computed(
    () =>
      rotates.value &&
      !paused.value &&
      !held.value &&
      onScreen.value &&
      tabVisible.value
  );
  /** Waiting to resume where it stopped: off screen or in a hidden tab. */
  const waiting = computed(
    () => rotates.value && !paused.value && !held.value && !running.value
  );
  // Bumped on every full (re)start, as a :key that restarts the chip's fill animation.
  const cycle = ref(0);

  let timer: ReturnType<typeof setTimeout> | undefined;
  let left = ms;
  let startedAt: number | undefined;

  function arm() {
    clearTimeout(timer);
    if (startedAt !== undefined) {
      left -= Date.now() - startedAt;
      startedAt = undefined;
    }
    if (held.value) {
      left = ms;
    }
    if (!running.value) {
      return;
    }
    if (left === ms) {
      cycle.value += 1;
    }
    startedAt = Date.now();
    timer = setTimeout(() => go(index.value + 1), left);
  }

  function go(i: number) {
    const n = toValue(count);
    index.value = n > 0 ? (i + n) % n : 0;
    clearTimeout(timer);
    startedAt = undefined;
    left = ms;
    arm();
  }

  function toggle() {
    paused.value = !paused.value;
  }

  function stop() {
    stopped.value = true;
  }

  watch(running, arm);
  watch(
    () => toValue(count),
    (n) => {
      if (index.value >= n) {
        go(0);
        return;
      }
      arm();
    }
  );

  const listeners: [string, (e: Event) => void][] = [
    [
      "focusin",
      () => {
        focused.value = true;
      },
    ],
    [
      "focusout",
      (e) => {
        const next = (e as FocusEvent).relatedTarget as Node | null;
        focused.value = !!next && !!el.value?.contains(next);
      },
    ],
    [
      "mouseenter",
      () => {
        hovered.value = true;
      },
    ],
    [
      "mouseleave",
      () => {
        hovered.value = false;
      },
    ],
  ];

  let observer: IntersectionObserver | undefined;
  const onVisibility = () => {
    tabVisible.value = document.visibilityState !== "hidden";
  };

  // The block may render after mount, or go when its cards do.
  function watchBlock(node: HTMLElement | null, old?: HTMLElement | null) {
    for (const [type, fn] of listeners) {
      old?.removeEventListener(type, fn);
      node?.addEventListener(type, fn);
    }
    observer?.disconnect();
    if (!node) {
      return;
    }
    if (!("IntersectionObserver" in window)) {
      onScreen.value = true;
      return;
    }
    observer ??= new IntersectionObserver(
      ([entry]) => {
        // At least half the block in view, as a tracked view.
        onScreen.value = entry.intersectionRatio >= 0.5;
      },
      { threshold: [0, 0.5, 1] }
    );
    observer.observe(node);
  }

  onMounted(() => {
    document.addEventListener("visibilitychange", onVisibility);
    onVisibility();
  });
  // Post flush: after the template ref is set, also on the first render.
  watch(el, watchBlock, { flush: "post" });

  onBeforeUnmount(() => {
    clearTimeout(timer);
    watchBlock(null, el.value);
    document.removeEventListener("visibilitychange", onVisibility);
  });

  return {
    cycle,
    el,
    go,
    index,
    onScreen,
    paused,
    reducedMotion,
    rotates,
    running,
    stop,
    stopped,
    toggle,
    waiting,
  };
}

export type Rotation = ReturnType<typeof useRotation>;
